import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Globe, CheckCircle2, AlertCircle, Save, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const URL_RE = /^https:\/\/[a-z0-9.\-]+(:\d+)?$/i;

const Field = ({ label, value, onChange, source, envValue, placeholder, testid }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <label className="text-sm font-semibold text-gray-800">{label}</label>
      <span
        data-testid={`${testid}-source`}
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded ${
          source === 'db'
            ? 'bg-emerald-100 text-emerald-800'
            : source === 'env'
            ? 'bg-gray-100 text-gray-700'
            : 'bg-amber-100 text-amber-800'
        }`}
      >
        source: {source}
      </span>
    </div>
    <input
      type="url"
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#659A9B] text-sm font-mono"
    />
    {source !== 'db' && (
      <p className="text-xs text-gray-500">
        Currently falling back to <code className="px-1 py-0.5 bg-gray-100 rounded text-[11px]">{envValue || '(unset)'}</code> from .env
      </p>
    )}
  </div>
);

const PublicUrlsSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [frontendBase, setFrontendBase] = useState('');
  const [backendBase, setBackendBase] = useState('');
  const [frontendSource, setFrontendSource] = useState('unset');
  const [backendSource, setBackendSource] = useState('unset');
  const [envFrontend, setEnvFrontend] = useState('');
  const [envBackend, setEnvBackend] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);

  const axiosCfg = { withCredentials: true };

  const fetchUrls = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${BACKEND_URL}/api/admin/settings/public-urls`, axiosCfg);
      setFrontendBase(r.data.frontend_public_base || '');
      setBackendBase(r.data.backend_public_base || '');
      setFrontendSource(r.data.frontend_source || 'unset');
      setBackendSource(r.data.backend_source || 'unset');
      setEnvFrontend(r.data.env_frontend_public_base || '');
      setEnvBackend(r.data.env_backend_public_base || '');
      setUpdatedAt(r.data.updated_at || null);
    } catch (e) {
      console.error('Failed to fetch public URL settings', e);
      setMessage({ type: 'error', text: 'Failed to load public URL settings.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUrls();
  }, [fetchUrls]);

  const validate = (v) => !v || URL_RE.test(v.trim());

  const handleSave = async () => {
    const fb = (frontendBase || '').trim();
    const bb = (backendBase || '').trim();
    if (!validate(fb) || !validate(bb)) {
      setMessage({ type: 'error', text: 'URLs must look like https://host (no path, no trailing slash).' });
      return;
    }
    if (/localhost|127\.0\.0\.1/.test(fb) || /localhost|127\.0\.0\.1/.test(bb)) {
      setMessage({ type: 'error', text: 'localhost / 127.0.0.1 are not allowed.' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const r = await axios.put(
        `${BACKEND_URL}/api/admin/settings/public-urls`,
        { frontend_public_base: fb, backend_public_base: bb },
        axiosCfg,
      );
      if (r.data?.success) {
        setFrontendBase(r.data.frontend_public_base || '');
        setBackendBase(r.data.backend_public_base || '');
        setFrontendSource(r.data.frontend_source || 'unset');
        setBackendSource(r.data.backend_source || 'unset');
        setMessage({ type: 'success', text: 'Public URLs saved. New emails, payment redirects and WhatsApp links will use these values immediately.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (e) {
      console.error('Failed to save public URLs', e);
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to save public URLs.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const r = await axios.put(
        `${BACKEND_URL}/api/admin/settings/public-urls`,
        { frontend_public_base: '', backend_public_base: '' },
        axiosCfg,
      );
      if (r.data?.success) {
        setFrontendBase(r.data.frontend_public_base || '');
        setBackendBase(r.data.backend_public_base || '');
        setFrontendSource(r.data.frontend_source || 'unset');
        setBackendSource(r.data.backend_source || 'unset');
        setMessage({ type: 'success', text: 'Cleared DB overrides — now falling back to .env values.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      }
    } catch (e) {
      console.error('Failed to reset public URLs', e);
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to reset public URLs.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section
        data-testid="public-urls-settings-card"
        className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
      >
        <div className="animate-pulse h-32 bg-gray-100 rounded-lg" />
      </section>
    );
  }

  return (
    <section
      data-testid="public-urls-settings-card"
      className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[#659A9B]/15 flex items-center justify-center flex-shrink-0">
          <Globe className="w-5 h-5 text-[#659A9B]" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold text-gray-900">Public URLs</h2>
          <p className="text-sm text-gray-600 mt-1">
            Absolute URLs used for outbound emails (booking confirmations, password resets),
            Razorpay payment-success redirects, WhatsApp deep-links, and ticket PDFs.
            DB values override <code>.env</code>; leave blank to fall back to <code>.env</code>.
          </p>
        </div>
      </div>

      <div className="space-y-4 border border-gray-200 rounded-lg p-4">
        <Field
          label="Frontend Public Base"
          value={frontendBase}
          onChange={setFrontendBase}
          source={frontendSource}
          envValue={envFrontend}
          placeholder="https://www.acftourism.com"
          testid="frontend-public-base-input"
        />
        <Field
          label="Backend Public Base"
          value={backendBase}
          onChange={setBackendBase}
          source={backendSource}
          envValue={envBackend}
          placeholder="https://www.acftourism.com"
          testid="backend-public-base-input"
        />

        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            data-testid="public-urls-save"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-[#1B375B] hover:bg-[#152b48] rounded-lg disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            data-testid="public-urls-reset"
            onClick={handleReset}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
            title="Clear DB overrides and fall back to .env"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to .env
          </button>
          {updatedAt && (
            <span className="text-xs text-gray-500 ml-auto">
              Last updated: {new Date(updatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {message.text && (
        <div
          data-testid="public-urls-message"
          className={`mt-4 p-3 rounded-lg flex items-start gap-2 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          )}
          <span>{message.text}</span>
        </div>
      )}
    </section>
  );
};

export default PublicUrlsSettings;
